import type {
  LogContext,
  LoggerInitConfig,
  LogLevel,
  NewRelicWindowInterface
} from "./types";

class LoggerClient {
  private applicationName: string = "Unknown Application";
  private logSource: string = "Unknown Source";
  private enableNewRelic: boolean = false;
  private enableConsole: boolean = true;
  private environment: string = "unknown";
  private isInitialized: boolean = false;
  private flowType: string = "";

  public initialize(config: LoggerInitConfig): void {
    try {
      this.applicationName = config.applicationName ?? "Unknown Application";
      this.logSource = config.logSource ?? "Unknown Source";
      this.enableNewRelic = config.enableNewRelic ?? false;
      this.enableConsole = config.enableConsole ?? true;
      this.environment = config.environment ?? "unknown";

      if (this.enableNewRelic) {
        this.validateNewRelicSetup();
      }

      this.isInitialized = true;
    } catch (error) {
      console.error("Failed to initialize logger:", error);
      this.enableConsole = true;
      this.isInitialized = true;
    }
  }

  public setFlowType(flowType: string): void {
    this.flowType = flowType;
  }

  public info(message: string, context?: LogContext): void {
    try {
      this.log("info", message, context);
    } catch (e) {
      this.handleLoggingError("info", message, e);
    }
  }

  public warn(message: string, context?: LogContext): void {
    try {
      this.log("warn", message, context);
    } catch (e) {
      this.handleLoggingError("warn", message, e);
    }
  }

  public error(
    message: string,
    errorOrContext?: Error | LogContext,
    context?: LogContext
  ): void {
    try {
      let errorObj: Error | undefined;
      let logContext: LogContext = {};
      if (errorOrContext instanceof Error) {
        errorObj = errorOrContext;
        logContext = context || {};
      } else if (errorOrContext) {
        logContext = errorOrContext;
      }
      this.log("error", message, logContext, errorObj);
    } catch (e) {
      this.handleLoggingError("error", message, e);
    }
  }

  public debug(message: string, context?: LogContext): void {
    try {
      this.log("debug", message, context);
    } catch (e) {
      this.handleLoggingError("debug", message, e);
    }
  }

  private get prefix(): string {
    const parts = [`[${this.applicationName}]`];
    if (this.flowType) {
      parts.push(`[${this.flowType}]`);
    }
    return parts.join("");
  }

  private log(
    level: LogLevel,
    message: string,
    context: LogContext = {},
    errorObj?: Error
  ): void {
    const logEntry = {
      level,
      message,
      timestamp: new Date().toISOString(),
      applicationName: this.applicationName,
      logSource: this.logSource,
      environment: this.environment,
      ...(this.flowType && { flowType: this.flowType }),
      ...context,
      ...(errorObj && {
        errorStack: errorObj.stack,
        errorMessage: errorObj.message
      })
    };

    if (this.enableConsole) this.logToConsole(level, message, logEntry);
    if (this.enableNewRelic && this.isInitialized)
      this.logToNewRelic(level, message, logEntry, errorObj);
  }

  private logToConsole(
    level: LogLevel,
    message: string,
    context: Record<string, unknown>
  ): void {
    try {
      const logFn =
        level === "error"
          ? console.error
          : level === "warn"
            ? console.warn
            : level === "debug"
              ? console.debug
              : console.info;
      logFn(this.prefix, message, context);
    } catch {
      /* silently fail */
    }
  }

  private logToNewRelic(
    level: LogLevel,
    message: string,
    logEntry: Record<string, unknown>,
    errorObj?: Error
  ): void {
    try {
      const nrWindow = window as unknown as NewRelicWindowInterface;
      if (!nrWindow?.newrelic) return;

      if (level === "error" && errorObj && nrWindow.newrelic.noticeError) {
        nrWindow.newrelic.noticeError(errorObj, logEntry);
      }

      if (nrWindow.newrelic.log) {
        nrWindow.newrelic.log(`[${level.toUpperCase()}] ${message}`, {
          level,
          customAttributes: logEntry
        });
      }
    } catch {
      /* silently fail â€” never break the app for logging */
    }
  }

  private validateNewRelicSetup(): void {
    try {
      if (!(window as unknown as NewRelicWindowInterface)?.newrelic) {
        console.warn(
          "New Relic is not available in window â€” disabling NR logging"
        );
        this.enableNewRelic = false;
      }
    } catch {
      this.enableNewRelic = false;
    }
  }

  private handleLoggingError(
    level: LogLevel,
    message: string,
    error: unknown
  ): void {
    try {
      console.error(
        `Logger error at ${level} for "${message}":`,
        error instanceof Error ? error.message : String(error)
      );
    } catch {
      /* ultimate fallback */
    }
  }
}

export const loggerClient = new LoggerClient();
